import EntityModel from '@/common/models/entity-model';
import SegmentDataModel from '@/common/models/segment-data-model';
import SegmentModel from '@/common/models/segment-model';
import SegmentCategoryModel from '@/common/models/segment-category-model';

export default class SegmentPageModel extends EntityModel {
    /**
     * @type {String}
     */
    name;

    /**
     * @type {Object.<string, SegmentCategoryModel>}
     */
    categories;

    /**
     * @type {SegmentCategoryModel}
     */
    $activeCategory;

    get label() {
        return SegmentDataModel.PagesStructure[this.name].label;
    }

    get types() {
        return SegmentDataModel.PagesStructure[this.name].types;
    }

    /**
     * @returns {SegmentCategoryModel|null}
     */
    get activeCategory() {
        if (this.$activeCategory) {
            return this.$activeCategory;
        }
        if (this.hasStarredSegments) {
            return Object.values(this.categories)
                .find(category => category.hasStarredSegments) || null;
        }
        // Activate first non-empty page.
        return Object.values(this.categories).find(category => category.containsSegments) || null;
    }

    get activeSegment() {
        const {activeCategory} = this;

        return activeCategory ? activeCategory.activeSegment : null;
    }

    get hasStarredSegments() {
        return this.starredSegments.length > 0;
    }

    get starredSegments() {
        return Object.values(this.categories).reduce((acc, cat) => [
            ...acc,
            ...cat.segments.filter(({isVisible, isStarred}) => isVisible && isStarred),
        ], []);
    }

    hydrate(
        {
            name = null,
            activeCategoryName = null,
            segments = [],
        } = {},
    ) {
        if (!SegmentDataModel.PagesStructure[name]) {
            throw new Error('Unknown segment page');
        }

        this.name = name;
        this.categories = {};

        SegmentDataModel.PagesStructure[name].categories.forEach((categoryName) => {
            this.categories[categoryName] = new SegmentCategoryModel({name: categoryName});
        });

        segments.forEach((segment) => {
            this.addSegment(segment);
        });

        if (activeCategoryName) {
            this.selectCategory(activeCategoryName);
        } else {
            this.deselectCategory();
        }
    }

    /**
     * @param {SegmentModel} segment
     */
    addSegment(segment) {
        if (!(segment instanceof SegmentModel)) {
            segment = new SegmentModel(segment);
        }

        const typeId = this.types.find(type => type === segment.data.type);

        if (!typeId) {
            throw new Error('Invalid segment type');
        }
        this.categories[SegmentDataModel.TypesStructure[typeId].category].addSegment(segment);
    }

    /**
     * @param {String} segmentId
     */
    removeSegment(segmentId, update) {
        const segment = this.findSegmentById(segmentId);

        if (!(segment instanceof SegmentModel)) {
            throw new Error('Segment not found');
        }
        const {type} = segment.data;
        const category = this.categories[SegmentDataModel.TypesStructure[type].category];
        category.removeSegment(segmentId, update);
        if (this.$activeCategory
            && this.$activeCategory.name === category.name
            && !category.containsSegments
        ) {
            this.deselectCategory();
        }
    }

    selectCategory(categoryName) {
        if (!this.categories[categoryName]) {
            throw new Error(`Category not found ${categoryName}`);
        }
        this.$activeCategory = this.categories[categoryName];

        const {activeCategory} = this;

        if (activeCategory) {
            activeCategory.deselectSegment();
            activeCategory.showHidden = false;
            activeCategory.searchQuery = '';
        }
    }

    deselectCategory() {
        this.$activeCategory = null;

        const {activeCategory} = this;

        if (activeCategory) {
            activeCategory.deselectSegment();
            activeCategory.showHidden = false;
            activeCategory.searchQuery = '';
        }
    }

    isCategoryActive(categoryName) {
        return this.activeCategory && this.activeCategory.name === categoryName;
    }

    findSegmentById(segmentId) {
        const segment = Object.values(this.categories).reduce(
            (memo, category) => memo || category.segments.find(({id}) => id === segmentId) || null,
            null,
        );
        return segment || null;
    }

    selectSegment(segmentId) {
        const segment = this.findSegmentById(segmentId);

        if (!(segment instanceof SegmentModel)) {
            throw new Error(`Segment not found ${segmentId}`);
        }
        this.selectCategory(SegmentDataModel.TypesStructure[segment.data.type].category);
        this.activeCategory.selectSegment(segmentId);
    }

    deselectSegment() {
        this.activeCategory.deselectSegment();
    }
}
